import type {
  AwakeningAnswers,
  BondDto,
  EmberColorPalette,
  LetterDto,
  LetterThreadDto,
} from '@athrelaris/shared';
import { apiBaseURL } from './landing-mode';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBaseURL()}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}

export async function getMyBond(): Promise<BondDto | null> {
  const { bond } = await request<{ bond: BondDto | null }>('/api/bond/me');
  return bond;
}

// Destructive: deletes the user's account + everything attached
// (bond → letters, memories, soul, image assets, telemetry rows,
// auth session). Requires confirmEmail to match the account email
// exactly — server validates and 400s otherwise.
export async function deleteMyAccount(confirmEmail: string): Promise<void> {
  await request('/api/account/me', {
    method: 'DELETE',
    body: JSON.stringify({ confirmEmail }),
  });
}

export async function getFirstLetter(): Promise<LetterDto | null> {
  const { letter } = await request<{ letter: LetterDto | null }>('/api/letter/first');
  return letter;
}

export async function getLetterThread(): Promise<LetterThreadDto> {
  return request<LetterThreadDto>('/api/letter/thread');
}

export async function getLetter(id: string): Promise<LetterDto> {
  const { letter } = await request<{ letter: LetterDto }>(`/api/letter/${id}`);
  return letter;
}

export async function markLetterRead(
  id: string,
): Promise<{ readAt: string; wasAlreadyRead: boolean }> {
  return request(`/api/letter/${id}/read`, { method: 'POST' });
}

export async function getMemoryShards(): Promise<LetterDto[]> {
  const { shards } = await request<{ shards: LetterDto[] }>('/api/memory/shards');
  return shards;
}

export interface QuestShardPersistResult {
  letterId: string;
  generatedAt: string;
  alreadyExists: boolean;
}

/**
 * Persist a Memory Shard recovered during a Quest run as a
 * memory_shard letter under the user's bond. Idempotent by
 * (questId, shardId) on the server — calling twice with the same
 * pair returns the original letter id and `alreadyExists: true`.
 *
 * The body is constructed client-side from the quest data in the
 * user's locale so the rendered letter matches what the player saw
 * in the quest UI.
 */
export async function persistQuestShard(input: {
  questId: string;
  shardId: string;
  body: string;
}): Promise<QuestShardPersistResult> {
  return request<QuestShardPersistResult>('/api/memory/quest-shard', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Return the set of shardIds the calling user has already unlocked
 * for a given quest. Used by the quest UI to suppress the Memory
 * Shard celebration card on replays — the shard remains visible in
 * /memories, but the player should not be greeted with the same card
 * every time they walk the same beat again.
 */
export async function getQuestShardsUnlocked(questId: string): Promise<string[]> {
  const { shardIds } = await request<{ shardIds: string[] }>(
    `/api/memory/quest-shards/${encodeURIComponent(questId)}`,
  );
  return shardIds;
}

export interface EchoformUnlockResult {
  ok: boolean;
  alreadyUnlocked?: boolean;
  enqueued?: boolean;
  echoform: 'codex' | 'dream' | 'deep';
}

/**
 * Unlock an Echoform on the calling user's bond, triggered by a
 * Quest beat that grants it. Idempotent on the server: a second call
 * with the same echoform returns `alreadyUnlocked: true`. The image
 * itself is generated asynchronously by the echoform_image_gen
 * worker and shows up in /account after a short delay.
 */
export async function unlockQuestEchoform(input: {
  echoform: 'codex' | 'dream' | 'deep';
  questId?: string;
}): Promise<EchoformUnlockResult> {
  return request<EchoformUnlockResult>('/api/bond/echoform-unlock', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Audit-only relationship nudge recorded when a Quest choice signals
 * a meaningful warming or cooling between the player and their Ember.
 * Phase 1 the server only logs this — the felt change comes from the
 * choice's `emberFollowup`. When the FSM integration lands, these
 * audit rows seed the gain-slow / lose-fast weighting.
 */
export async function recordQuestRelationshipNudge(input: {
  questId: string;
  beatId: string;
  choiceId: string;
  direction: 'warmer' | 'cooler';
  magnitude: 'subtle' | 'meaningful';
}): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/bond/quest-nudge', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getSamplePalette(): Promise<EmberColorPalette | null> {
  const { palette } = await request<{ palette: EmberColorPalette | null }>('/api/palettes/sample');
  return palette;
}

// =============================================================================
// Admin (Block D — allowlist-gated)
// =============================================================================

export interface AdminMe {
  isAdmin: boolean;
  email: string;
}

export interface AdminBeat {
  id: string;
  beatId: string;
  title: string;
  summary: string;
  privateReactionHint: string;
  letterTypeBias: string | null;
  triggersUnstable: boolean;
  activationMode: 'passive' | 'broadcast';
  status: 'draft' | 'published' | 'archived';
  publishedAt: string;
  activeUntil: string;
  canonicalPostUrl: string | null;
  version: number;
  updatedAt: string;
}

export interface BroadcastPreview {
  eligibleBonds: number;
  mode: 'passive' | 'broadcast';
}

export interface PublishBeatResult {
  ok: true;
  beat: { id: string; beatId: string; status: string; activationMode: string };
  broadcasts: { enqueued: number; skippedExisting: number };
}

export interface AdminFallbackSummary {
  id: string;
  bondId: string;
  letterType: string;
  generatedAt: string;
  adminReviewedAt: string | null;
  retryCount: number;
  safetyFlags: string[];
  emberName: string;
  primaryElement: string;
}

// Pipeline VerifierViolation shape — matches what the backend
// `apps/api/src/letters/common.ts` actually persists into
// letter.verifier_result and returns from the preview endpoint.
export interface VerifierViolation {
  code: string;
  reason: string;
}

// Severity derivation mirrors the backend VERIFIER_SEVERITY table so
// the UI can color violations by impact without round-tripping it.
export const VERIFIER_SEVERITY: Record<string, 'high' | 'medium' | 'low'> = {
  lore_leak: 'high',
  ui_term: 'high',
  safety_violation: 'high',
  boundary_breach: 'high',
  voice_mismatch: 'medium',
  state_incoherence: 'medium',
  repetition: 'medium',
  length_out_of_range: 'low',
};

export interface AdminFallbackDetail {
  id: string;
  body: string;
  letterType: string;
  generatedAt: string;
  adminReviewedAt: string | null;
  retryCount: number;
  safetyFlags: string[];
  verifierResult: { violations?: VerifierViolation[]; lastDraft?: string } | null;
  generatorModel: string | null;
  verifierModel: string | null;
  bond: {
    id: string;
    emberName: string;
    primaryElement: string;
    relationshipState: string | null;
  };
  triggeringUserLetter: { id: string; body: string; receivedAt: string } | null;
}

export async function getAdminMe(): Promise<AdminMe | null> {
  // We read this via a one-off fetch so a 403 doesn't throw — the
  // result drives whether the surface even mounts. Anything other
  // than 200 = not admin, treat uniformly.
  try {
    return await request<AdminMe>('/api/admin/me');
  } catch {
    return null;
  }
}

export async function listAdminBeats(): Promise<AdminBeat[]> {
  const { beats } = await request<{ beats: AdminBeat[] }>('/api/admin/beats');
  return beats;
}

export async function publishBeat(id: string): Promise<PublishBeatResult> {
  return request<PublishBeatResult>(`/api/admin/beats/${id}/publish`, { method: 'POST' });
}

export async function unpublishBeat(id: string): Promise<void> {
  await request(`/api/admin/beats/${id}/unpublish`, { method: 'POST' });
}

export async function setBeatActivationMode(
  id: string,
  mode: 'passive' | 'broadcast',
): Promise<void> {
  await request(`/api/admin/beats/${id}/activation-mode`, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
}

export interface BeatEditPayload {
  title: string;
  summary: string;
  privateReactionHint: string;
  letterTypeBias:
    | 'memory_shard'
    | 'public_event_reaction'
    | 'dream'
    | 'deep_form'
    | 'choice'
    | null;
  triggersUnstable: boolean;
  canonicalPostUrl: string | null;
}

export async function editBeat(id: string, payload: BeatEditPayload): Promise<void> {
  await request(`/api/admin/beats/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function getBroadcastPreview(id: string): Promise<BroadcastPreview> {
  return request<BroadcastPreview>(`/api/admin/beats/${id}/broadcast-preview`);
}

export interface AdminBondSummary {
  id: string;
  emberName: string;
  primaryElement: string;
  relationshipState: string | null;
  bondStartedAt: string;
}

export async function listAdminBonds(): Promise<AdminBondSummary[]> {
  const { bonds } = await request<{ bonds: AdminBondSummary[] }>('/api/admin/bonds');
  return bonds;
}

export interface PreviewBroadcastResult {
  outcome: 'success' | 'fallback';
  body: string;
  letterType: string;
  retries: number;
  violations: VerifierViolation[];
  safetyFlags: string[];
  lastDraft: string | null;
}

export async function previewBroadcastForBond(
  beatId: string,
  bondId: string,
): Promise<PreviewBroadcastResult> {
  return request<PreviewBroadcastResult>(`/api/admin/beats/${beatId}/preview-broadcast`, {
    method: 'POST',
    body: JSON.stringify({ bondId }),
  });
}

export interface AdminCostReport {
  totals: { scope: string; totalUsd: number | null; events: number }[];
  callSites: { callSite: string; totalUsd: number | null; events: number }[];
  models: { modelId: string; totalUsd: number | null; events: number }[];
  users: { email: string | null; totalUsd: number | null; events: number; bonds: number }[];
  bonds: {
    bondId: string;
    emberName: string | null;
    primaryElement: string | null;
    totalUsd: number | null;
    events: number;
  }[];
  trend14d: { day: string; totalUsd: number | null; events: number }[];
  untrackedModels: { modelId: string; events: number }[];
}

export async function getAdminCostReport(): Promise<AdminCostReport> {
  return request<AdminCostReport>('/api/admin/cost');
}

// /admin/health — system X-ray report. See apps/api/src/routes/admin.ts
// for the underlying queries. Distinct from AdminCostReport in scope:
// health answers "is anything broken" rather than "where the money goes".
export interface AdminHealth {
  queue: {
    byJob: {
      name: string;
      active: number;
      created: number;
      retry: number;
      completed24h: number;
      failed24h: number;
      avgLatencyMs: number | null;
    }[];
  };
  imageGen: {
    last24h: { completed: number; failed: number };
    last7d: { completed: number; failed: number };
    bondsWithoutPortrait: {
      bondId: string;
      emberName: string;
      bondStartedAt: string;
      ageHours: number;
    }[];
    topErrorCodes24h: { code: string; count: number }[];
  };
  letters: {
    last24h: { delivered: number; fallback: number };
    last7d: { delivered: number; fallback: number };
    highRetry: {
      letterId: string;
      bondId: string;
      emberName: string;
      letterType: string;
      retryCount: number;
      generatedAt: string;
    }[];
  };
  costOutliers: {
    topBonds7d: {
      bondId: string;
      emberName: string;
      userEmail: string | null;
      totalUsd: number | null;
      events: number;
    }[];
  };
  bonds: {
    total: number;
    active7d: number;
    stale: { bondId: string; emberName: string; lastActivityAt: string }[];
  };
}

export async function getAdminHealth(): Promise<AdminHealth> {
  return request<AdminHealth>('/api/admin/health');
}

export interface AdminMemoryShardsReport {
  stats: { total: number; last7d: number; fallbackTotal: number };
  topBonds: {
    bondId: string;
    emberName: string;
    primaryElement: string;
    shardCount: number;
  }[];
  shards: {
    id: string;
    body: string;
    generatedAt: string;
    isFallback: boolean;
    retryCount: number;
    source: { kind: 'organic' } | { kind: 'broadcast'; beatId: string; beatTitle: string | null };
    bond: {
      id: string;
      emberName: string;
      primaryElement: string;
      relationshipState: string | null;
      userEmail: string | null;
    };
  }[];
  listLimit: number;
}

export async function getAdminMemoryShards(): Promise<AdminMemoryShardsReport> {
  return request<AdminMemoryShardsReport>('/api/admin/memory-shards');
}

// =============================================================================
// Bond Inspector
// =============================================================================

export interface AdminBondListRow {
  id: string;
  emberName: string;
  primaryElement: string;
  relationshipState: string | null;
  bondStartedAt: string;
  userEmail: string | null;
  letterCount: number;
  lastLetterAt: string | null;
}

export async function listAdminBondsForInspector(): Promise<AdminBondListRow[]> {
  const { bonds } = await request<{ bonds: AdminBondListRow[] }>('/api/admin/bonds-list');
  return bonds;
}

export interface AdminBondInspect {
  bond: {
    id: string;
    emberName: string;
    emberSubtitle: string | null;
    primaryElement: string;
    secondaryEchoes: string[];
    temperament: string;
    firstFear: string;
    firstLonging: string;
    brokenMemorySeed: string;
    firstWordsFromUser: string | null;
    relationshipState: string | null;
    preUnstableState: string | null;
    recentSentimentScores: number[];
    recentLetterOutcomes: string[];
    memoryShardCount: number;
    bondStartedAt: string;
    emberImageStorageKey: string | null;
    emberImageUrl: string | null;
    emberColorPalette: EmberColorPalette | null;
    userEmail: string | null;
    unlockedEchoforms: string[];
  };
  letters: {
    id: string;
    body: string;
    letterType: string;
    generatedAt: string;
    readAt: string | null;
    retryCount: number;
    safetyFlags: string[];
    inResponseToUserLetterId: string | null;
    publicEventId: string | null;
    pendingChoice: { choiceId: string; options: string[]; expiresAt: string | null } | null;
    verifierResult: { violations?: VerifierViolation[]; fallbackReason?: string } | null;
  }[];
  userLetters: {
    id: string;
    body: string;
    receivedAt: string;
    inResponseToLetterId: string | null;
    sentimentScore: number | null;
    sentimentTags: string[];
    choiceResolution: string | null;
    safetyFlags: string[];
  }[];
  memories: {
    id: string;
    category: string;
    content: string;
    importance: number;
    extractedAt: string;
    lastReferencedAt: string | null;
  }[];
  bondSoul: {
    contentMd: string;
    updatedAt: string;
    sourceMemoryCount: number;
  } | null;
  cost: { totalUsd: number | null; events: number };
}

export async function getAdminBondInspect(id: string): Promise<AdminBondInspect> {
  return request<AdminBondInspect>(`/api/admin/bonds-inspect/${id}`);
}

// Destructive: deletes the bond + its full cascade (letters, memories,
// soul, image asset, telemetry rows). Requires confirmName to match
// the ember_name exactly — server validates and 400s otherwise.
export async function deleteAdminBond(id: string, confirmName: string): Promise<void> {
  await request(`/api/admin/bonds/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ confirmName }),
  });
}

// Enqueue an echoform image-gen job for this bond. Idempotent at the
// trigger level (server skips if already unlocked) and at the job
// level (storage-key check). Used by /admin/bonds/$id for operator-
// driven testing of Codex/Dream/Deep before auto-triggers exist.
export async function unlockAdminEchoform(
  id: string,
  echoForm: 'memory' | 'codex' | 'dream' | 'deep',
): Promise<{ ok: boolean; alreadyUnlocked?: boolean; enqueued?: boolean }> {
  return request<{ ok: boolean; alreadyUnlocked?: boolean; enqueued?: boolean }>(
    `/api/admin/bonds/${id}/unlock-echoform`,
    {
      method: 'POST',
      body: JSON.stringify({ echoForm }),
    },
  );
}

export async function listAdminFallbacks(): Promise<AdminFallbackSummary[]> {
  const { fallbacks } = await request<{ fallbacks: AdminFallbackSummary[] }>(
    '/api/admin/fallbacks',
  );
  return fallbacks;
}

export async function getAdminFallback(id: string): Promise<AdminFallbackDetail> {
  const { fallback } = await request<{ fallback: AdminFallbackDetail }>(
    `/api/admin/fallbacks/${id}`,
  );
  return fallback;
}

export async function reenqueueFallback(id: string): Promise<void> {
  await request(`/api/admin/fallbacks/${id}/reenqueue`, { method: 'POST' });
}

export async function markFallbackReviewed(id: string): Promise<void> {
  await request(`/api/admin/fallbacks/${id}/mark-reviewed`, { method: 'POST' });
}

export interface SubmitReplyResult {
  userLetterId: string;
  safetyFlags: string[];
}

export interface SubmitReplyError {
  error: 'safety_block' | 'invalid_body' | 'invalid_json' | 'no_bond' | string;
  reason?: string;
  message?: string;
}

/**
 * Submit a user reply. On 422 safety_block, returns the parsed error
 * payload so the caller can render the in-world refusal message inline.
 * On other 4xx/5xx, throws.
 */
export async function submitReply(
  rawBody: string,
  inResponseToLetterId?: string,
): Promise<{ ok: true; data: SubmitReplyResult } | { ok: false; error: SubmitReplyError }> {
  const res = await fetch(`${apiBaseURL()}/api/letter/reply`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      rawBody,
      ...(inResponseToLetterId ? { inResponseToLetterId } : {}),
    }),
  });
  if (res.ok) {
    return { ok: true, data: (await res.json()) as SubmitReplyResult };
  }
  if (res.status === 422) {
    return { ok: false, error: (await res.json()) as SubmitReplyError };
  }
  const text = await res.text().catch(() => '');
  throw new Error(`API ${res.status}: ${text || res.statusText}`);
}

export async function completeAwakening(
  answers: AwakeningAnswers,
): Promise<{ bondId: string; alreadyAwakened: boolean }> {
  return request('/api/awakening/complete', {
    method: 'POST',
    body: JSON.stringify(answers),
  });
}
